import React, {useMemo} from 'react';
import {useQuery} from 'react-query';
import {Box, Chip, Tooltip, Typography} from '@mui/material';
import OwnersService from '../../../apis/OwnersService';
import DialogHelpers from './data-stream-edit-dialog/helpers';

/**
 * Grid cell for data_stream.partition_configs: one chip per rule, `<what> → <owner>`.
 *
 * Parsing goes through the editor's helper so every legacy shape renders. An unparseable
 * value keeps its raw text rather than rendering blank.
 */

const ruleText = (rule) => {
  if (rule.match === 'pen') return 'By pen';
  if (rule.match === 'reassign') return 'All rows';
  return rule.value === '' ? '(no value)' : String(rule.value);
};

// An id alone says nothing about who the rows went to. The name leads, with the id kept
// because that is what the config actually stores. Falls back to the bare id while the
// owner list is still loading, or for an id that no longer resolves to an owner.
const ownerLabel = (ownerId, namesById) => {
  const name = namesById.get(Number(ownerId));
  return name ? `${name} (${ownerId})` : String(ownerId);
};

// Valueless matches name the type only, to avoid "All rows: All rows → Acme (17122)".
const ruleTitle = (rule, namesById) => {
  const label = DialogHelpers.MATCH_LABELS[rule.match] || rule.match;
  return DialogHelpers.VALUELESS_MATCHES.includes(rule.match)
    ? `${label} → ${ownerLabel(rule.owner_id, namesById)}`
    : `${label}: ${ruleText(rule)} → ${ownerLabel(rule.owner_id, namesById)}`;
};

const PartitionConfigsCell = ({value}) => {
  // Same key the owner dropdowns use, so the grid reads their cache rather than refetching.
  const {data: owners} = useQuery('getOwners', OwnersService.getAllSortedByName, {
    staleTime: Infinity,
  });

  const namesById = useMemo(
    () => new Map((owners || []).map((owner) => [Number(owner.id), owner.name])),
    [owners]
  );

  if (value === null || value === undefined || value === '') {
    return <Typography variant='body2' color='textSecondary'>—</Typography>;
  }

  const {ok, rows} = DialogHelpers.parsePartitionConfigs(value);

  if (!ok || rows.length === 0) {
    const raw = typeof value === 'object' ? JSON.stringify(value) : String(value);
    return (
      <Tooltip title={raw}>
        <Typography variant='body2' color='error' noWrap>{raw}</Typography>
      </Tooltip>
    );
  }

  // One rule per line; the grid uses auto row height to fit them.
  return (
    <Tooltip
      title={
        <Box component='span' sx={{display: 'block', whiteSpace: 'pre-line'}}>
          {rows.map((rule) => ruleTitle(rule, namesById)).join('\n')}
        </Box>
      }
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 0.25,
        py: 0.5,
        width: '100%',
      }}>
        {rows.map((rule, index) => (
          <Chip
            key={index}
            size='small'
            variant='outlined'
            label={`${ruleText(rule)} → ${ownerLabel(rule.owner_id, namesById)}`}
            sx={{maxWidth: '100%'}}
          />
        ))}
      </Box>
    </Tooltip>
  );
};

export default PartitionConfigsCell;
